import React, { useState, useEffect, useCallback, useRef } from 'react';
import { UtensilsCrossed, Plus, Trash2, Edit2, Check, X, Calendar, Users, Clock, ChevronDown, ChevronUp, ExternalLink, Settings, BookOpen, LayoutGrid, Copy, AlertTriangle, Upload, Map } from 'lucide-react';

function ConfirmInline({ message, onConfirm, onCancel }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '6px 10px' }}>
      <AlertTriangle size={13} color="#ea580c" />
      <span style={{ fontSize: 12, color: '#9a3412' }}>{message}</span>
      <button onClick={onConfirm} style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Supprimer</button>
      <button onClick={onCancel} style={{ background: 'white', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px 8px', fontSize: 12, cursor: 'pointer' }}>Annuler</button>
    </div>
  );
}

const API = 'https://qbdp-backend-production.up.railway.app/api';
const token = () => sessionStorage.getItem('qbdp_token');
const h = () => ({ Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' });

const SERVICES = [
  { value: 'midi', label: 'Midi', color: '#0d9488' },
  { value: 'soir', label: 'Soir', color: '#7c3aed' },
  { value: 'brunch', label: 'Brunch', color: '#d97706' },
];

const ZONES = ['salle', 'terrasse', 'bar', 'vip', 'privatif'];
const CATEGORIES = [
  { value: 'entree', label: 'Entrée' },
  { value: 'plat', label: 'Plat' },
  { value: 'dessert', label: 'Dessert' },
  { value: 'boisson', label: 'Boisson' },
];

const STATUTS_RESA = {
  confirmee:   { label: 'Confirmée',   color: '#16a34a', bg: '#dcfce7' },
  en_attente:  { label: 'En attente',  color: '#d97706', bg: '#fef9c3' },
  presente:    { label: 'Présente',    color: '#1a56db', bg: '#eff6ff' },
  annulee:     { label: 'Annulée',     color: '#dc2626', bg: '#fee2e2' },
};

const HEURES_MIDI = ['11:30','12:00','12:30','13:00','13:30','14:00','14:30'];
const HEURES_SOIR = ['18:30','19:00','19:30','20:00','20:30','21:00','21:30','22:00'];

function Input({ label, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>{label}</label>}
      <input
        {...props}
        style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 13, outline: 'none', ...props.style }}
      />
    </div>
  );
}

function Select({ label, children, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>{label}</label>}
      <select
        {...props}
        style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 13, background: 'white', outline: 'none', ...props.style }}
      >
        {children}
      </select>
    </div>
  );
}

function Btn({ variant = 'primary', children, ...props }) {
  const styles = {
    primary: { background: '#1a56db', color: 'white', border: 'none' },
    ghost:   { background: 'white', color: '#374151', border: '1px solid #e5e7eb' },
    danger:  { background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca' },
    success: { background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0' },
  };
  return (
    <button
      {...props}
      style={{ ...styles[variant], borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, ...props.style }}
    >
      {children}
    </button>
  );
}

// ─── ONGLET RÉSERVATIONS ──────────────────────────────────────────────────────
function OngletReservations({ restaurant, tables }) {
  const [date, setDate]       = useState(new Date().toISOString().split('T')[0]);
  const [service, setService] = useState('midi');
  const [resas, setResas]     = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]       = useState({ externe_nom: '', externe_email: '', externe_telephone: '', heure: '12:00', couverts: 2, table_id: '', notes: '' });
  const [editId, setEditId]   = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    const r = await fetch(`${API}/restaurant/reservations?date=${date}&service=${service}`, { headers: h() });
    setResas(await r.json());
  }, [date, service]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const body = { ...form, date, service };
    if (editId) {
      await fetch(`${API}/restaurant/reservations/${editId}`, { method: 'PUT', headers: h(), body: JSON.stringify(body) });
    } else {
      await fetch(`${API}/restaurant/reservations`, { method: 'POST', headers: h(), body: JSON.stringify(body) });
    }
    setShowForm(false);
    setEditId(null);
    setForm({ externe_nom: '', externe_email: '', externe_telephone: '', heure: '12:00', couverts: 2, table_id: '', notes: '' });
    load();
  };

  const changeStatut = async (id, statut) => {
    await fetch(`${API}/restaurant/reservations/${id}/statut`, { method: 'PUT', headers: h(), body: JSON.stringify({ statut }) });
    load();
  };

  const del = async (id) => {
    await fetch(`${API}/restaurant/reservations/${id}`, { method: 'DELETE', headers: h() });
    setConfirmDel(null);
    load();
  };

  const heures = service === 'midi' ? HEURES_MIDI : service === 'soir' ? HEURES_SOIR : [...HEURES_MIDI, ...HEURES_SOIR];
  const totalCouverts = resas.filter(r => r.statut !== 'annulee').reduce((s, r) => s + parseInt(r.couverts || 0), 0);

  return (
    <div>
      {/* Filtres */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Input label="Date" type="date" value={date} onChange={e => setDate(e.target.value)} />
        <div style={{ display: 'flex', gap: 6 }}>
          {SERVICES.map(s => (
            <button key={s.value} onClick={() => setService(s.value)}
              style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${service === s.value ? s.color : '#e5e7eb'}`, background: service === s.value ? s.color : 'white', color: service === s.value ? 'white' : '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
              {s.label}
            </button>
          ))}
        </div>
        <Btn onClick={() => { setShowForm(true); setEditId(null); setForm({ externe_nom: '', externe_email: '', externe_telephone: '', heure: heures[0] || '12:00', couverts: 2, table_id: '', notes: '' }); }}>
          <Plus size={14} /> Nouvelle réservation
        </Btn>
      </div>

      {/* Stats rapides */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Réservations', value: resas.filter(r => r.statut !== 'annulee').length, color: '#1a56db' },
          { label: 'Couverts', value: totalCouverts, color: '#7c3aed' },
          { label: 'Annulées', value: resas.filter(r => r.statut === 'annulee').length, color: '#dc2626' },
        ].map(s => (
          <div key={s.label} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 16px', display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Formulaire */}
      {showForm && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', marginBottom: 12 }}>
            {editId ? 'Modifier la réservation' : 'Nouvelle réservation'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <Input label="Nom *" value={form.externe_nom} onChange={e => setForm(f => ({ ...f, externe_nom: e.target.value }))} placeholder="Dupont Martin" />
            <Input label="Email" type="email" value={form.externe_email} onChange={e => setForm(f => ({ ...f, externe_email: e.target.value }))} placeholder="email@exemple.fr" />
            <Input label="Téléphone" value={form.externe_telephone} onChange={e => setForm(f => ({ ...f, externe_telephone: e.target.value }))} placeholder="06 12 34 56 78" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <Select label="Heure" value={form.heure} onChange={e => setForm(f => ({ ...f, heure: e.target.value }))}>
              {heures.map(h => <option key={h} value={h}>{h}</option>)}
            </Select>
            <Input label="Couverts" type="number" min="1" max="20" value={form.couverts} onChange={e => setForm(f => ({ ...f, couverts: parseInt(e.target.value) }))} />
            <Select label="Table" value={form.table_id} onChange={e => setForm(f => ({ ...f, table_id: e.target.value }))}>
              <option value="">Pas de table assignée</option>
              {tables.filter(t => t.active).map(t => (
                <option key={t.id} value={t.id}>Table {t.numero} — {t.capacite} places ({t.zone})</option>
              ))}
            </Select>
          </div>
          <Input label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Anniversaire, allergie..." style={{ marginBottom: 12 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn onClick={save} disabled={!form.externe_nom}><Check size={14} /> Enregistrer</Btn>
            <Btn variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }}><X size={14} /> Annuler</Btn>
          </div>
        </div>
      )}

      {/* Liste */}
      {resas.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '40px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
          Aucune réservation pour ce créneau
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {resas.map(r => {
            const st = STATUTS_RESA[r.statut] || STATUTS_RESA.confirmee;
            return (
              <div key={r.id} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 48, height: 48, background: '#f3f4f6', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, color: '#374151' }}>
                  {r.heure}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                    {r.client_nom || r.externe_nom}
                    {r.table_numero && <span style={{ marginLeft: 8, fontSize: 12, color: '#6b7280', fontWeight: 400 }}>Table {r.table_numero} ({r.table_zone})</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', gap: 12, marginTop: 2 }}>
                    <span><Users size={11} style={{ marginRight: 3 }} />{r.couverts} couvert{r.couverts > 1 ? 's' : ''}</span>
                    {r.externe_telephone && <span>{r.externe_telephone}</span>}
                    {r.nb_plats > 0 && <span style={{ color: '#7c3aed' }}>✓ {r.nb_plats} plat(s) pré-commandé(s)</span>}
                    {r.notes && <span>{r.notes}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ background: st.bg, color: st.color, fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20 }}>{st.label}</span>
                  {r.statut !== 'presente' && r.statut !== 'annulee' && (
                    <Btn variant="success" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => changeStatut(r.id, 'presente')}>
                      <Check size={11} /> Présent
                    </Btn>
                  )}
                  {r.statut !== 'annulee' && (
                    <Btn variant="ghost" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => {
                      setEditId(r.id);
                      setForm({ externe_nom: r.externe_nom || r.client_nom || '', externe_email: r.externe_email || '', externe_telephone: r.externe_telephone || '', heure: r.heure, couverts: r.couverts, table_id: r.table_id || '', notes: r.notes || '' });
                      setShowForm(true);
                    }}>
                      <Edit2 size={11} />
                    </Btn>
                  )}
                  {confirmDel === r.id
                    ? <ConfirmInline message="Supprimer ?" onConfirm={() => del(r.id)} onCancel={() => setConfirmDel(null)} />
                    : <Btn variant="danger" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => setConfirmDel(r.id)}><Trash2 size={11} /></Btn>
                  }
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── ONGLET MENUS ─────────────────────────────────────────────────────────────
function OngletMenus() {
  const [menus, setMenus]         = useState([]);
  const [expanded, setExpanded]   = useState({});
  const [newMenuForm, setNewMenuForm] = useState({ nom: '', service: 'midi' });
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [editPlat, setEditPlat]   = useState(null);
  const [platForm, setPlatForm]   = useState({ nom: '', description: '', prix: '', categorie: 'plat' });
  const [confirmDelMenu, setConfirmDelMenu] = useState(null);
  const [confirmDelPlat, setConfirmDelPlat] = useState(null);
  const [showImport, setShowImport]   = useState(false);
  const [importText, setImportText]   = useState('');
  const [importData, setImportData]   = useState(null);
  const [importError, setImportError] = useState('');
  const [importing, setImporting]     = useState(false);

  const parseImportText = (text) => {
    if (!text.trim()) { setImportData(null); setImportError(''); return; }
    try {
      if (text.trim().startsWith('[') || text.trim().startsWith('{')) {
        const raw = JSON.parse(text);
        setImportData(Array.isArray(raw) ? raw : [raw]);
        setImportError('');
      } else {
        const lines = text.trim().split('\n').filter(l => l.trim());
        const menusMap = {};
        const dataLines = lines[0].toLowerCase().includes('menu') ? lines.slice(1) : lines;
        for (const line of dataLines) {
          const [menuNom, service, categorie, platNom, description, prix] = line.split(';').map(c => c.trim());
          if (!menuNom || !platNom) continue;
          const key = `${menuNom}|||${service || 'midi'}`;
          if (!menusMap[key]) menusMap[key] = { nom: menuNom, service: service || 'midi', plats: [] };
          menusMap[key].plats.push({ nom: platNom, categorie: categorie || 'plat', description: description || '', prix: prix ? parseFloat(prix.replace(',', '.')) : null });
        }
        const arr = Object.values(menusMap);
        if (!arr.length) { setImportError('Aucune donnée valide — vérifiez le format'); setImportData(null); return; }
        setImportData(arr);
        setImportError('');
      }
    } catch (e) { setImportError('Format invalide : ' + e.message); setImportData(null); }
  };

  const handleImportFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setImportText(ev.target.result); parseImportText(ev.target.result); };
    reader.readAsText(file, 'utf-8');
  };

  const doImport = async () => {
    if (!importData?.length) return;
    setImporting(true);
    try {
      const r = await fetch(`${API}/restaurant/import-menus`, { method: 'POST', headers: h(), body: JSON.stringify({ menus: importData }) });
      const data = await r.json();
      if (data.error) { setImportError(data.error); }
      else { setShowImport(false); setImportText(''); setImportData(null); setImportError(''); load(); }
    } catch (e) { setImportError(e.message); }
    setImporting(false);
  };

  const load = useCallback(async () => {
    const r = await fetch(`${API}/restaurant/menus`, { headers: h() });
    const data = await r.json();
    setMenus(data);
    if (data.length > 0 && Object.keys(expanded).length === 0) {
      setExpanded({ [data[0].id]: true });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addMenu = async () => {
    await fetch(`${API}/restaurant/menus`, { method: 'POST', headers: h(), body: JSON.stringify(newMenuForm) });
    setShowNewMenu(false);
    setNewMenuForm({ nom: '', service: 'midi' });
    load();
  };

  const toggleMenu = async (menu) => {
    await fetch(`${API}/restaurant/menus/${menu.id}`, { method: 'PUT', headers: h(), body: JSON.stringify({ ...menu, disponible: !menu.disponible }) });
    load();
  };

  const delMenu = async (id) => {
    await fetch(`${API}/restaurant/menus/${id}`, { method: 'DELETE', headers: h() });
    setConfirmDelMenu(null);
    load();
  };

  const savePlat = async () => {
    if (editPlat?.plat) {
      await fetch(`${API}/restaurant/plats/${editPlat.plat.id}`, { method: 'PUT', headers: h(), body: JSON.stringify(platForm) });
    } else {
      await fetch(`${API}/restaurant/plats`, { method: 'POST', headers: h(), body: JSON.stringify({ ...platForm, menu_id: editPlat.menu_id }) });
    }
    setEditPlat(null);
    setPlatForm({ nom: '', description: '', prix: '', categorie: 'plat' });
    load();
  };

  const delPlat = async (id) => {
    await fetch(`${API}/restaurant/plats/${id}`, { method: 'DELETE', headers: h() });
    setConfirmDelPlat(null);
    load();
  };

  const openEditPlat = (menuId, plat = null) => {
    setEditPlat({ menu_id: menuId, plat });
    setPlatForm(plat ? { nom: plat.nom, description: plat.description || '', prix: plat.prix || '', categorie: plat.categorie || 'plat' } : { nom: '', description: '', prix: '', categorie: 'plat' });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 14 }}>
        <Btn variant="ghost" onClick={() => setShowImport(v => !v)}><Upload size={14} /> Importer</Btn>
        <Btn onClick={() => setShowNewMenu(v => !v)}><Plus size={14} /> Ajouter un menu</Btn>
      </div>

      {showImport && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', marginBottom: 6 }}>Importer des menus</div>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12, lineHeight: 1.7 }}>
            <strong>Format CSV</strong> (séparateur <code style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: 4 }}>;</code>) — 1 ligne = 1 plat :<br/>
            <code style={{ background: '#f3f4f6', padding: '3px 8px', borderRadius: 4, fontSize: 11, display: 'inline-block', marginTop: 4 }}>Menu du Midi;midi;entree;Salade niçoise;Thon olives;9.50</code><br/>
            Colonnes : <em>Menu · Service (midi/soir/brunch) · Catégorie (entree/plat/dessert/boisson) · Nom · Description · Prix</em><br/>
            <strong>Format JSON</strong> : <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>[{'{'}nom,service,plats:[{'{'}nom,categorie,prix,description{'}'}]{'}'}]</code>
          </p>
          <textarea
            value={importText}
            onChange={e => { setImportText(e.target.value); parseImportText(e.target.value); }}
            placeholder="Collez votre CSV ou JSON ici…"
            style={{ width: '100%', height: 120, border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 12, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
          />
          <div style={{ marginTop: 8, marginBottom: 12 }}>
            <label style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, color: '#374151' }}>
              <Upload size={13} /> Charger un fichier (.csv, .json)
              <input type="file" accept=".csv,.json,.txt" onChange={handleImportFile} style={{ display: 'none' }} />
            </label>
          </div>
          {importError && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{importError}</div>}
          {importData && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#15803d', marginBottom: 8 }}>
                Aperçu : {importData.length} menu(s) — {importData.reduce((s, m) => s + (m.plats?.length || 0), 0)} plat(s)
              </div>
              {importData.map((m, i) => (
                <div key={i} style={{ fontSize: 12, color: '#374151', marginBottom: 6 }}>
                  <span style={{ fontWeight: 600 }}>{m.nom}</span>
                  <span style={{ color: '#9ca3af', marginLeft: 6 }}>({m.service || 'midi'}) — {m.plats?.length || 0} plat(s)</span>
                  <div style={{ marginLeft: 12, marginTop: 2 }}>
                    {(m.plats || []).slice(0, 4).map((p, j) => (
                      <span key={j} style={{ color: '#6b7280', marginRight: 12, fontSize: 11 }}>· {p.nom}{p.prix ? ` ${p.prix}€` : ''}</span>
                    ))}
                    {(m.plats?.length || 0) > 4 && <span style={{ color: '#9ca3af', fontSize: 11 }}>+{m.plats.length - 4} autres</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn onClick={doImport} disabled={!importData || importing}>
              {importing ? 'Import en cours…' : <><Check size={14} /> Importer {importData ? `(${importData.reduce((s,m)=>s+(m.plats?.length||0),0)} plats)` : ''}</>}
            </Btn>
            <Btn variant="ghost" onClick={() => { setShowImport(false); setImportText(''); setImportData(null); setImportError(''); }}>
              <X size={14} /> Annuler
            </Btn>
          </div>
        </div>
      )}

      {showNewMenu && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
            <Input label="Nom du menu" value={newMenuForm.nom} onChange={e => setNewMenuForm(f => ({ ...f, nom: e.target.value }))} placeholder="Menu du Midi, Carte Brasserie…" />
            <Select label="Service" value={newMenuForm.service} onChange={e => setNewMenuForm(f => ({ ...f, service: e.target.value }))}>
              {SERVICES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn onClick={addMenu} disabled={!newMenuForm.nom}><Check size={14} /></Btn>
              <Btn variant="ghost" onClick={() => setShowNewMenu(false)}><X size={14} /></Btn>
            </div>
          </div>
        </div>
      )}

      {menus.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '40px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
          Aucun menu — ajoutez-en un pour commencer
        </div>
      ) : menus.map(menu => {
        const sv = SERVICES.find(s => s.value === menu.service) || SERVICES[0];
        const isOpen = expanded[menu.id];
        const platsByCategorie = CATEGORIES.map(c => ({
          ...c,
          plats: (menu.plats || []).filter(p => p.categorie === c.value),
        })).filter(c => c.plats.length > 0);

        return (
          <div key={menu.id} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 10, overflow: 'hidden' }}>
            {/* Header menu */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', cursor: 'pointer', borderBottom: isOpen ? '1px solid #f3f4f6' : 'none' }}
              onClick={() => setExpanded(e => ({ ...e, [menu.id]: !e[menu.id] }))}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
                <BookOpen size={16} color={sv.color} />
                <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{menu.nom}</span>
                <span style={{ background: sv.color + '20', color: sv.color, fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 20 }}>{sv.label}</span>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>{(menu.plats || []).length} plat(s)</span>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <button onClick={e => { e.stopPropagation(); toggleMenu(menu); }}
                  style={{ padding: '3px 10px', borderRadius: 6, border: `1px solid ${menu.disponible ? '#bbf7d0' : '#e5e7eb'}`, background: menu.disponible ? '#dcfce7' : '#f9fafb', color: menu.disponible ? '#16a34a' : '#9ca3af', fontSize: 11, cursor: 'pointer' }}>
                  {menu.disponible ? 'Disponible' : 'Indisponible'}
                </button>
                <Btn variant="ghost" style={{ padding: '4px 8px' }} onClick={e => { e.stopPropagation(); openEditPlat(menu.id); }}>
                  <Plus size={13} />
                </Btn>
                {confirmDelMenu === menu.id
                  ? <span onClick={e => e.stopPropagation()}><ConfirmInline message="Supprimer ce menu ?" onConfirm={() => delMenu(menu.id)} onCancel={() => setConfirmDelMenu(null)} /></span>
                  : <Btn variant="danger" style={{ padding: '4px 8px' }} onClick={e => { e.stopPropagation(); setConfirmDelMenu(menu.id); }}><Trash2 size={13} /></Btn>
                }
                {isOpen ? <ChevronUp size={15} color="#9ca3af" /> : <ChevronDown size={15} color="#9ca3af" />}
              </div>
            </div>

            {isOpen && (
              <div style={{ padding: '12px 16px' }}>
                {/* Formulaire plat */}
                {editPlat?.menu_id === menu.id && (
                  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 14 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: '#374151' }}>
                      {editPlat.plat ? 'Modifier le plat' : 'Ajouter un plat'}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <Input label="Nom du plat *" value={platForm.nom} onChange={e => setPlatForm(f => ({ ...f, nom: e.target.value }))} placeholder="Magret de canard…" />
                      <Input label="Prix (€)" type="number" step="0.5" value={platForm.prix} onChange={e => setPlatForm(f => ({ ...f, prix: e.target.value }))} placeholder="18.50" />
                      <Select label="Catégorie" value={platForm.categorie} onChange={e => setPlatForm(f => ({ ...f, categorie: e.target.value }))}>
                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </Select>
                    </div>
                    <Input label="Description" value={platForm.description} onChange={e => setPlatForm(f => ({ ...f, description: e.target.value }))} placeholder="Courte description…" style={{ marginBottom: 10 }} />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn onClick={savePlat} disabled={!platForm.nom}><Check size={13} /> Enregistrer</Btn>
                      <Btn variant="ghost" onClick={() => setEditPlat(null)}><X size={13} /> Annuler</Btn>
                    </div>
                  </div>
                )}

                {platsByCategorie.length === 0 ? (
                  <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
                    Aucun plat — cliquez sur + pour en ajouter
                  </div>
                ) : platsByCategorie.map(cat => (
                  <div key={cat.value} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{cat.label}</div>
                    {cat.plats.map(plat => (
                      <div key={plat.id} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: 13, fontWeight: 500, color: plat.disponible ? '#111827' : '#9ca3af', textDecoration: plat.disponible ? 'none' : 'line-through' }}>{plat.nom}</span>
                          {plat.description && <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>{plat.description}</span>}
                        </div>
                        {plat.prix && <span style={{ fontSize: 13, fontWeight: 600, color: '#1a56db' }}>{parseFloat(plat.prix).toFixed(2)} €</span>}
                        <Btn variant="ghost" style={{ padding: '3px 8px' }} onClick={() => openEditPlat(menu.id, plat)}>
                          <Edit2 size={12} />
                        </Btn>
                        {confirmDelPlat === plat.id
                          ? <ConfirmInline message="Supprimer ?" onConfirm={() => delPlat(plat.id)} onCancel={() => setConfirmDelPlat(null)} />
                          : <Btn variant="danger" style={{ padding: '3px 8px' }} onClick={() => setConfirmDelPlat(plat.id)}><Trash2 size={12} /></Btn>
                        }
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── ONGLET TABLES ────────────────────────────────────────────────────────────
function OngletTables() {
  const [tables, setTables]       = useState([]);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState({ numero: '', capacite: 2, zone: 'salle' });
  const [editId, setEditId]       = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [viewMode, setViewMode]   = useState('liste');
  const [resasToday, setResasToday] = useState({ midi: [], soir: [] });

  const load = useCallback(async () => {
    const r = await fetch(`${API}/restaurant/tables`, { headers: h() });
    setTables(await r.json());
  }, []);

  const loadPlan = useCallback(async () => {
    const today = new Date().toISOString().split('T')[0];
    const [rMidi, rSoir] = await Promise.all([
      fetch(`${API}/restaurant/reservations?date=${today}&service=midi`, { headers: h() }).then(r => r.json()),
      fetch(`${API}/restaurant/reservations?date=${today}&service=soir`, { headers: h() }).then(r => r.json()),
    ]);
    setResasToday({ midi: Array.isArray(rMidi) ? rMidi : [], soir: Array.isArray(rSoir) ? rSoir : [] });
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (viewMode === 'plan') loadPlan(); }, [viewMode, loadPlan]);

  const save = async () => {
    if (editId) {
      await fetch(`${API}/restaurant/tables/${editId}`, { method: 'PUT', headers: h(), body: JSON.stringify(form) });
    } else {
      await fetch(`${API}/restaurant/tables`, { method: 'POST', headers: h(), body: JSON.stringify(form) });
    }
    setShowForm(false); setEditId(null); setForm({ numero: '', capacite: 2, zone: 'salle' }); load();
  };

  const del = async (id) => {
    await fetch(`${API}/restaurant/tables/${id}`, { method: 'DELETE', headers: h() });
    setConfirmDel(null);
    load();
  };

  const byZone = ZONES.map(z => ({ zone: z, tables: tables.filter(t => t.zone === z) })).filter(z => z.tables.length > 0);

  const getResaStatus = (tableId, service) => {
    const resas = resasToday[service] || [];
    return resas.find(r => String(r.table_id) === String(tableId)) || null;
  };
  const statusColor = (resa) => !resa ? '#16a34a' : resa.statut === 'presente' ? '#1a56db' : '#d97706';
  const statusBg    = (resa) => !resa ? '#dcfce7' : resa.statut === 'presente' ? '#eff6ff' : '#fef9c3';
  const statusLabel = (resa) => !resa ? 'Libre' : resa.statut === 'presente' ? 'Présent·e' : (resa.client_nom || resa.externe_nom || 'Réservé');

  return (
    <div>
      {/* Top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 2, background: '#f3f4f6', borderRadius: 8, padding: 3 }}>
          {[['liste', <LayoutGrid size={13} />, 'Liste'], ['plan', <Map size={13} />, 'Plan du jour']].map(([k, icon, lbl]) => (
            <button key={k} onClick={() => setViewMode(k)}
              style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: viewMode === k ? 'white' : 'transparent', color: viewMode === k ? '#111827' : '#6b7280', fontSize: 12, fontWeight: viewMode === k ? 600 : 400, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, boxShadow: viewMode === k ? '0 1px 3px rgba(0,0,0,.08)' : 'none' }}>
              {icon}{lbl}
            </button>
          ))}
        </div>
        <Btn onClick={() => { setShowForm(v => !v); setEditId(null); setForm({ numero: '', capacite: 2, zone: 'salle' }); }}>
          <Plus size={14} /> Ajouter une table
        </Btn>
      </div>

      {showForm && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
            <Input label="Numéro / nom *" value={form.numero} onChange={e => setForm(f => ({ ...f, numero: e.target.value }))} placeholder="1, T1, Bar-2…" />
            <Input label="Capacité (places)" type="number" min="1" max="30" value={form.capacite} onChange={e => setForm(f => ({ ...f, capacite: parseInt(e.target.value) }))} />
            <Select label="Zone" value={form.zone} onChange={e => setForm(f => ({ ...f, zone: e.target.value }))}>
              {ZONES.map(z => <option key={z} value={z}>{z.charAt(0).toUpperCase() + z.slice(1)}</option>)}
            </Select>
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn onClick={save} disabled={!form.numero}><Check size={14} /></Btn>
              <Btn variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }}><X size={14} /></Btn>
            </div>
          </div>
        </div>
      )}

      {tables.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '40px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
          Aucune table configurée
        </div>
      ) : viewMode === 'plan' ? (
        <>
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: 12, color: '#6b7280', alignItems: 'center' }}>
            {[['#16a34a','#dcfce7','Libre'],['#d97706','#fef9c3','Réservé'],['#1a56db','#eff6ff','Présent·e']].map(([c,bg,lbl]) => (
              <span key={lbl} style={{ display:'flex', alignItems:'center', gap:5 }}>
                <span style={{ width:10,height:10,borderRadius:'50%',background:c,border:`2px solid ${bg}`,display:'inline-block' }} />{lbl}
              </span>
            ))}
            <Btn variant="ghost" style={{ padding:'4px 10px', fontSize:11, marginLeft:'auto' }} onClick={loadPlan}>Actualiser</Btn>
          </div>
          {byZone.map(({ zone, tables: zTables }) => (
            <div key={zone} style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>{zone.charAt(0).toUpperCase() + zone.slice(1)}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {zTables.filter(t => t.active).map(t => {
                  const resaMidi = getResaStatus(t.id, 'midi');
                  const resaSoir = getResaStatus(t.id, 'soir');
                  const midiC = statusColor(resaMidi), soirC = statusColor(resaSoir);
                  const midiB = statusBg(resaMidi),   soirB = statusBg(resaSoir);
                  const border = resaMidi || resaSoir ? (resaMidi?.statut === 'presente' || resaSoir?.statut === 'presente' ? '#bfdbfe' : '#fde68a') : '#e5e7eb';
                  return (
                    <div key={t.id} style={{ width: 148, background: 'white', border: `1.5px solid ${border}`, borderRadius: 12, padding: '12px 14px' }}>
                      <div style={{ fontWeight: 700, fontSize: 20, color: '#111827' }}>{t.numero}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10 }}>{t.capacite} places · {zone}</div>
                      {[['midi','Midi',resaMidi,midiC,midiB],['soir','Soir',resaSoir,soirC,soirB]].map(([svc,lbl,resa,c,bg]) => (
                        <div key={svc} style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                          <span style={{ width:7, height:7, borderRadius:'50%', background:c, flexShrink:0 }} />
                          <span style={{ fontSize:10, color:'#9ca3af', width:24 }}>{lbl}</span>
                          <span style={{ fontSize:10, background:bg, color:c, padding:'1px 7px', borderRadius:20, fontWeight:600, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {statusLabel(resa)}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      ) : (
        <>
          {byZone.map(({ zone, tables: zTables }) => (
            <div key={zone} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{zone.charAt(0).toUpperCase() + zone.slice(1)}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                {zTables.map(t => (
                  <div key={t.id} style={{ background: 'white', border: `1px solid ${t.active ? '#e5e7eb' : '#fecaca'}`, borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 18, color: '#111827' }}>Table {t.numero}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>{t.capacite} places</div>
                      </div>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <button onClick={() => { setEditId(t.id); setForm({ numero: t.numero, capacite: t.capacite, zone: t.zone, active: t.active }); setShowForm(true); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
                          <Edit2 size={13} />
                        </button>
                        {confirmDel === t.id
                          ? <ConfirmInline message="Supprimer ?" onConfirm={() => del(t.id)} onCancel={() => setConfirmDel(null)} />
                          : <button onClick={() => setConfirmDel(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 4 }}><Trash2 size={13} /></button>
                        }
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ─── ONGLET PARAMÈTRES ────────────────────────────────────────────────────────
function OngletParametres({ restaurant, onUpdate }) {
  const [form, setForm] = useState({
    nom: restaurant?.nom || '',
    slug: restaurant?.slug || '',
    description: restaurant?.description || '',
    telephone: restaurant?.telephone || '',
    email: restaurant?.email || '',
    adresse: restaurant?.adresse || '',
    site_web: restaurant?.site_web || '',
  });
  const [saved, setSaved] = useState(false);

  // Pré-remplir depuis Personnalisation si champs vides
  useEffect(() => {
    const isVierge = !restaurant?.telephone && !restaurant?.adresse && (restaurant?.nom === 'Mon Restaurant' || !restaurant?.nom);
    if (!isVierge) return;
    fetch(`${API}/personnalisation/parametres`, { headers: h() })
      .then(r => r.json())
      .then(d => {
        setForm(f => ({
          ...f,
          nom: f.nom === 'Mon Restaurant' || !f.nom ? (d.nom_etablissement || f.nom) : f.nom,
          telephone: f.telephone || d.telephone_etablissement || '',
          email: f.email || d.email_reponse || '',
          adresse: f.adresse || [d.adresse_etablissement, d.code_postal, d.ville_etablissement].filter(Boolean).join(', ') || '',
          site_web: f.site_web || d.site_web || '',
        }));
      })
      .catch(() => {});
  }, [restaurant]);

  const save = async () => {
    const r = await fetch(`${API}/restaurant`, { method: 'PUT', headers: h(), body: JSON.stringify(form) });
    const data = await r.json();
    if (data.error) { alert(data.error); return; }
    onUpdate(data);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const publicUrl = `${window.location.origin}/r/${form.slug || restaurant?.slug}`;

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e40af', marginBottom: 6 }}>Page publique de réservation</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{publicUrl}</span>
          <button onClick={() => navigator.clipboard.writeText(publicUrl)}
            style={{ background: 'white', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#1a56db', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Copy size={12} /> Copier
          </button>
          <a href={publicUrl} target="_blank" rel="noreferrer"
            style={{ background: '#1a56db', color: 'white', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
            <ExternalLink size={12} /> Voir
          </a>
        </div>
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #dbeafe', fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>
          💡 <strong style={{ color: '#1e40af' }}>Astuce :</strong> ouvrez la page et faites un <strong>clic droit → Créer un QR code</strong> (Chrome) pour générer un QR code à imprimer et afficher à l'entrée de votre établissement. Vous pouvez aussi <strong>copier-coller le lien</strong> directement dans vos notifications push, emails et SMS pour permettre à vos clients de réserver en ligne.
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Input label="Nom du restaurant *" value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} />
        <div>
          <Input label="Slug (URL)" value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))} placeholder="mon-restaurant" />
          <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0' }}>Uniquement des lettres minuscules, chiffres et tirets</p>
        </div>
        <Input label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Une courte description visible sur la page publique" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Input label="Téléphone" value={form.telephone} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))} />
          <Input label="Email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
        </div>
        <Input label="Adresse" value={form.adresse} onChange={e => setForm(f => ({ ...f, adresse: e.target.value }))} />
        <Input label="Site web" type="url" value={form.site_web} onChange={e => setForm(f => ({ ...f, site_web: e.target.value }))} placeholder="https://www.qbdp.fr" />
        <Btn onClick={save} style={{ alignSelf: 'flex-start', marginTop: 4 }}>
          {saved ? <><Check size={14} /> Enregistré !</> : <><Check size={14} /> Enregistrer</>}
        </Btn>
      </div>
    </div>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────────────────────────────────────
export default function Restaurant() {
  const [onglet, setOnglet]       = useState('reservations');
  const [restaurant, setRestaurant] = useState(null);
  const [tables, setTables]       = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/restaurant`, { headers: h() }).then(r => r.json()),
      fetch(`${API}/restaurant/tables`, { headers: h() }).then(r => r.json()),
    ]).then(([resto, tbls]) => {
      setRestaurant(resto);
      setTables(tbls);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: '#9ca3af', fontSize: 13 }}>
      Chargement…
    </div>
  );

  const ONGLETS = [
    { key: 'reservations', label: 'Réservations', icon: Calendar },
    { key: 'menus', label: 'Menus & Carte', icon: BookOpen },
    { key: 'tables', label: 'Tables', icon: LayoutGrid },
    { key: 'parametres', label: 'Paramètres', icon: Settings },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <UtensilsCrossed size={22} color="#1a56db" /> {restaurant?.nom || 'Restaurant'}
          </h1>
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Gestion des réservations et de la carte</p>
        </div>
        {restaurant?.slug && (
          <a href={`/r/${restaurant.slug}`} target="_blank" rel="noreferrer"
            style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', fontSize: 12, color: '#374151', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
            <ExternalLink size={13} /> Page de réservation
          </a>
        )}
      </div>

      {/* Onglets */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 4 }}>
        {ONGLETS.map(o => {
          const Icon = o.icon;
          const active = onglet === o.key;
          return (
            <button key={o.key} onClick={() => setOnglet(o.key)}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 0', borderRadius: 7, border: 'none', background: active ? '#1a56db' : 'transparent', color: active ? 'white' : '#6b7280', fontSize: 13, fontWeight: active ? 600 : 400, cursor: 'pointer', transition: 'all .15s' }}>
              <Icon size={14} />{o.label}
            </button>
          );
        })}
      </div>

      {/* Contenu */}
      {onglet === 'reservations' && <OngletReservations restaurant={restaurant} tables={tables} />}
      {onglet === 'menus'        && <OngletMenus />}
      {onglet === 'tables'       && <OngletTables />}
      {onglet === 'parametres'   && <OngletParametres restaurant={restaurant} onUpdate={r => setRestaurant(r)} />}
    </div>
  );
}
